import { MCP_ICONS } from './mcpIcons'

const MCP_URL = 'https://mcp.payhook.link/'

const CLAUDE_INSTALL_URL =
  'https://claude.ai/customize/connectors?modal=add-custom-connector' +
  '&connectorName=Payhook' +
  `&connectorUrl=${encodeURIComponent(MCP_URL)}`

const CURSOR_INSTALL_URL =
  'cursor://anysphere.cursor-deeplink/mcp/install' +
  '?name=Payhook' +
  `&config=${btoa(JSON.stringify({ url: MCP_URL }))}`

export const MCP_CLIENTS = [
  {
    id: 'claude',
    label: 'Install Agent in Claude',
    menuLabel: 'Claude',
    action: 'link',
    href: CLAUDE_INSTALL_URL
  },
  {
    id: 'chatgpt',
    label: 'Install Agent in ChatGPT',
    menuLabel: 'ChatGPT',
    action: 'copy',
    copyText: MCP_URL,
    pasteHint: 'Copy this URL and paste it in ChatGPT under Settings → Connectors.'
  },
  {
    id: 'cursor',
    label: 'Install Agent in Cursor',
    menuLabel: 'Cursor',
    action: 'link',
    href: CURSOR_INSTALL_URL
  },
  {
    id: 'replit',
    label: 'Install Agent in Replit',
    menuLabel: 'Replit',
    action: 'copy',
    copyText: MCP_URL,
    pasteHint: 'Copy this URL and paste it in Replit under Integrations → Agent.'
  },
  {
    id: 'lovable',
    label: 'Install Agent in Lovable',
    menuLabel: 'Lovable',
    action: 'copy',
    copyText: MCP_URL,
    pasteHint: 'Copy this URL and paste it in Lovable under Connectors.'
  },
  {
    id: 'gemini',
    label: 'Install Agent in Gemini',
    menuLabel: 'Gemini',
    action: 'copy',
    copyText: MCP_URL,
    pasteHint: 'Copy this URL and paste it in Gemini under Agent setup.'
  }
]

const HERO_MCP_ORDER = ['claude', 'chatgpt', 'cursor', 'replit', 'lovable', 'gemini']

export async function copyText (text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'absolute'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  const copied = document.execCommand('copy')
  document.body.removeChild(textarea)
  if (!copied) throw new Error('Copy failed')
}

export function flashButton (button, message) {
  const label = button.querySelector('.mcp-install__label, .hero-mcp-cta__label')
  const toolTitle = button.querySelector('.hero__tool-card-title')
  const target = label || toolTitle || button
  const previous = target.textContent
  target.textContent = message
  button.disabled = true
  window.setTimeout(() => {
    target.textContent = previous
    button.disabled = false
  }, 2200)
}

let copyDialogEl = null

function ensureCopyDialog () {
  if (copyDialogEl) return copyDialogEl

  copyDialogEl = document.createElement('dialog')
  copyDialogEl.className = 'mcp-install-dialog'
  copyDialogEl.id = 'mcp-copy-dialog'
  copyDialogEl.innerHTML = `
    <div class="mcp-install-dialog__panel">
      <div class="mcp-install-dialog__header">
        <h2 class="mcp-install-dialog__title" id="mcp-copy-dialog-title">Install Payhook Agent</h2>
        <button type="button" class="mcp-install-dialog__close" id="mcp-copy-dialog-close" aria-label="Close">×</button>
      </div>
      <p class="mcp-install-dialog__intro" id="mcp-copy-dialog-intro"></p>
      <pre class="prompt-block mcp-install-dialog__script"><code id="mcp-copy-dialog-script"></code></pre>
      <p class="mcp-install-dialog__hint">If your deployment uses a bearer token, add it when the client prompts you.</p>
      <div class="mcp-install-dialog__footer">
        <button type="button" class="btn btn--primary" id="mcp-copy-dialog-copy">Copy URL</button>
      </div>
    </div>
  `

  document.body.appendChild(copyDialogEl)

  const closeBtn = copyDialogEl.querySelector('#mcp-copy-dialog-close')
  const copyBtn = copyDialogEl.querySelector('#mcp-copy-dialog-copy')

  closeBtn?.addEventListener('click', () => copyDialogEl.close())
  copyDialogEl.addEventListener('click', (event) => {
    const panel = copyDialogEl.querySelector('.mcp-install-dialog__panel')
    if (panel && !panel.contains(event.target)) copyDialogEl.close()
  })

  copyBtn?.addEventListener('click', async () => {
    const text = copyDialogEl.dataset.copyText || ''
    try {
      await copyText(text)
      flashButton(copyBtn, 'Copied!')
    } catch {
      flashButton(copyBtn, 'Copy failed')
    }
  })

  return copyDialogEl
}

function openCopyDialog (client) {
  const dialog = ensureCopyDialog()
  const title = dialog.querySelector('#mcp-copy-dialog-title')
  const intro = dialog.querySelector('#mcp-copy-dialog-intro')
  const script = dialog.querySelector('#mcp-copy-dialog-script')
  const copyBtn = dialog.querySelector('#mcp-copy-dialog-copy')

  dialog.dataset.copyText = client.copyText || ''
  if (title) title.textContent = client.label
  if (intro) {
    intro.textContent =
      client.pasteHint || 'Copy this URL and paste it into your Agent setup.'
  }
  if (script) script.textContent = client.copyText || ''
  if (copyBtn) copyBtn.textContent = 'Copy URL'

  if (typeof dialog.showModal === 'function') dialog.showModal()
  else dialog.setAttribute('open', '')
}

function createInstallButton (client) {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'btn btn--ghost btn--sm mcp-install__btn'
  button.dataset.mcpClient = client.id

  const icon = document.createElement('span')
  icon.className = 'mcp-install__icon'
  icon.setAttribute('aria-hidden', 'true')
  icon.innerHTML = MCP_ICONS[client.id] || ''

  const label = document.createElement('span')
  label.className = 'mcp-install__label'
  label.textContent = client.label

  button.appendChild(icon)
  button.appendChild(label)

  button.addEventListener('click', () => {
    runMcpClientAction(client)
  })

  return button
}

export function getMcpClient (id) {
  return MCP_CLIENTS.find((client) => client.id === id)
}

export function runMcpClientAction (client, feedbackButton) {
  if (!client) return

  if (client.action === 'link') {
    window.open(client.href, '_blank', 'noopener,noreferrer')
    if (feedbackButton) flashButton(feedbackButton, 'Opening…')
    return
  }

  openCopyDialog(client)
}

function heroMcpRoot (el) {
  return el?.closest('[data-hero-mcp-cta]')
}

const PREFERS_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)').matches

function setHeroMcpMenuOpen (root, open) {
  if (!root) return
  const trigger = root.querySelector('.hero-mcp-cta__trigger')
  const menu = root.querySelector('.hero-mcp-cta__menu')
  root.classList.toggle('is-open', open)
  if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false')
  // Hover-capable devices show/hide the menu purely via CSS :hover/:focus-within.
  // Only the touch/click-to-toggle path needs the hidden attribute managed here.
  if (menu && !PREFERS_HOVER) menu.hidden = !open
}

function closeHeroMcpMenu (root) {
  setHeroMcpMenuOpen(root, false)
}

function createHeroMenuOption (client) {
  const item = document.createElement('button')
  item.type = 'button'
  item.className = 'hero-mcp-cta__option'
  item.role = 'menuitem'
  item.dataset.mcpClient = client.id

  const icon = document.createElement('span')
  icon.className = 'hero-mcp-cta__icon'
  icon.setAttribute('aria-hidden', 'true')
  icon.innerHTML = MCP_ICONS[client.id] || ''

  const label = document.createElement('span')
  label.className = 'hero-mcp-cta__label'
  label.textContent = client.menuLabel || client.label

  item.appendChild(icon)
  item.appendChild(label)

  item.addEventListener('click', (event) => {
    event.stopPropagation()
    runMcpClientAction(client, item)
    closeHeroMcpMenu(heroMcpRoot(item))
  })

  return item
}

function initHeroMcpCta () {
  const root = document.querySelector('[data-hero-mcp-cta]')
  if (!root || root.dataset.heroMcpBound === '1') return
  root.dataset.heroMcpBound = '1'

  const trigger = root.querySelector('.hero-mcp-cta__trigger')
  const menu = root.querySelector('.hero-mcp-cta__menu')
  if (!trigger || !menu) return

  HERO_MCP_ORDER.forEach((id) => {
    const client = getMcpClient(id)
    if (client) menu.appendChild(createHeroMenuOption(client))
  })

  trigger.addEventListener('click', (event) => {
    if (PREFERS_HOVER) return
    event.preventDefault()
    setHeroMcpMenuOpen(root, !root.classList.contains('is-open'))
  })

  document.addEventListener('click', (event) => {
    if (!root.classList.contains('is-open')) return
    if (root.contains(event.target)) return
    closeHeroMcpMenu(root)
  })

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeHeroMcpMenu(root)
  })

  if (!PREFERS_HOVER) {
    menu.hidden = true
  }
}

export function initMcpInstall () {
  initHeroMcpCta()

  document.querySelectorAll('[data-mcp-install]').forEach((root) => {
    if (root.dataset.mcpInstallBound === '1') return
    root.dataset.mcpInstallBound = '1'
    root.classList.add('mcp-install')

    const hint = document.createElement('p')
    hint.className = 'mcp-install__hint'
    hint.innerHTML =
      'Payhook Agent at <code>mcp.payhook.link</code>. If your deployment uses a bearer token, add it when the client prompts you.'

    const actions = document.createElement('div')
    actions.className = 'mcp-install__actions'
    actions.setAttribute('role', 'group')
    actions.setAttribute('aria-label', 'Add Payhook Agent')

    MCP_CLIENTS.forEach((client) => {
      actions.appendChild(createInstallButton(client))
    })

    root.appendChild(actions)
    root.appendChild(hint)
  })
}
